import api, { sendFile } from '../helpers/api';
import { takeEvery, call, put, all, select } from 'redux-saga/effects';

import * as bucketConstants from '../constants/buckets';
import * as bucketSelectors from '../selectors/bucketSelectors';
import * as bucketSettingsSelectors from '../selectors/bucketSettingsSelectors';
import * as userSelectors from '../selectors/userSelectors';
import { getErrorMessage } from '../helpers/Util';

import BackendPaths from '../helpers/BackendPaths';
import * as organizationSelectors from '../selectors/organizationSelectors';

function* fetchBucketMembers() {
    const bucketId = yield select(bucketSelectors.getCurrentBucketId);
    yield put({
        type: bucketConstants.FETCH_BUCKET_MEMBERS_REQUEST,
        payload: { bucketId },
    });
    try {
        const response = yield call(
            api,
            BackendPaths.bucketMembers.getPath({ bucketId }),
        );
        yield put({
            type: bucketConstants.FETCH_BUCKET_MEMBERS_SUCCESS,
            payload: { members: response, bucketId },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: bucketConstants.FETCH_BUCKET_MEMBERS_FAILURE,
            payload: { error: getErrorMessage(response), bucketId },
        });
    }
}

function* watchFetchBucketMembers() {
    yield takeEvery(
        bucketConstants.FETCH_CURRENT_BUCKET_MEMBERS_REQUEST,
        fetchBucketMembers,
    );
}

function* addBucketMembers(action) {
    const { usersToBeAdded, bucketId } = action.payload;
    try {
        const memberResponses = [];
        for (let index = 0; index < usersToBeAdded.length; index += 1) {
            const { memberId, isAdmin } = usersToBeAdded[index];
            const member = yield call(
                api,
                BackendPaths.bucketMember.getPath(),
                'POST',
                {
                    groupId: bucketId,
                    memberId,
                    isAdmin,
                },
            );
            memberResponses.push(member);
        }
        yield put({
            type: bucketConstants.ADD_BUCKET_MEMBERS_SUCCESS,
            payload: { members: memberResponses, bucketId },
        });
    } catch (e) {
        yield put({
            type: bucketConstants.ADD_BUCKET_MEMBERS_FAILURE,
        });
    }
}

function* watchAddBucketMembers() {
    yield takeEvery(
        bucketConstants.ADD_BUCKET_MEMBERS_REQUEST,
        addBucketMembers,
    );
}

function* addMembersToBuckets(action) {
    const { memberIds, bucketIds } = action.payload;
    const organizationId = yield select(
        organizationSelectors.getCurrentOrganizationId,
    );
    try {
        const membershipResponses = yield call(
            api,
            BackendPaths.bucketsMembers.getPath(),
            'POST',
            {
                organizationId,
                memberIds,
                groupIds: bucketIds,
                skipUnidentifiedGroups: true,
            },
        );
        yield put({
            type: bucketConstants.ADD_USERS_TO_BUCKETS_SUCCESS,
            payload: { membershipResponses, bucketIds, memberIds },
        });
    } catch (e) {
        yield put({
            type: bucketConstants.ADD_USERS_TO_BUCKETS_FAILURE,
        });
    }
}

function* watchAddMembersToBuckets() {
    yield takeEvery(
        bucketConstants.ADD_USERS_TO_BUCKETS_REQUEST,
        addMembersToBuckets,
    );
}

function* updateBucket(action) {
    const { id, name, description } = action.payload.bucket;

    try {
        const response = yield call(
            api,
            BackendPaths.bucketUpdate.getPath({ bucketId: id }),
            'POST',
            {
                name,
                description,
            },
        );
        yield put({
            type: bucketConstants.UPDATE_BUCKET_SUCCESS,
            payload: response,
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: bucketConstants.UPDATE_BUCKET_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchUpdateBucket() {
    yield takeEvery(bucketConstants.UPDATE_BUCKET_REQUEST, updateBucket);
}

function* updateBucketSettings() {
    const name = yield select(bucketSettingsSelectors.getName);
    const newName = name.trim();
    const visibleInApplications = yield select(
        bucketSettingsSelectors.getAppsIds,
    );
    const bucketId = yield select(bucketSelectors.getCurrentBucketId);
    try {
        const response = yield call(
            api,
            BackendPaths.bucketUpdate.getPath({ bucketId }),
            'POST',
            {
                name: !newName ? name : newName,
                visibleInApplications,
            },
        );
        yield put({
            type: bucketConstants.UPDATE_BUCKET_SETTINGS_SUCCESS,
            payload: response,
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: bucketConstants.UPDATE_BUCKET_SETTINGS_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchUpdateBucketSettings() {
    yield takeEvery(
        bucketConstants.UPDATE_BUCKET_SETTINGS_REQUEST,
        updateBucketSettings,
    );
}

function* updateBucketAvatar(action) {
    const { avatar, bucketId } = action.payload;
    try {
        const response = yield call(
            sendFile,
            BackendPaths.identity.getPath({ identityId: bucketId }),
            'POST',
            {
                file: avatar,
            },
        );
        yield put({
            type: bucketConstants.UPDATE_BUCKET_AVATAR_SUCCESS,
            payload: { avatars: response, bucketId },
        });
    } catch (e) {
        const response = yield e.json();
        yield put({
            type: bucketConstants.UPDATE_BUCKET_AVATAR_FAILURE,
            error: true,
            payload: getErrorMessage(response),
        });
    }
}

function* watchUpdateBucketAvatar() {
    yield takeEvery(
        bucketConstants.UPDATE_BUCKET_AVATAR_REQUEST,
        updateBucketAvatar,
    );
}

function* changeMembersRoles(action) {
    const { membersIds, isAdmin } = action.payload;
    const bucketId = yield select(bucketSelectors.getCurrentBucketId);
    try {
        const memberResponses = [];
        for (let index = 0; index < membersIds.length; index += 1) {
            const memberId = membersIds[index];
            const member = yield call(
                api,
                BackendPaths.bucketMembersUpdate.getPath(),
                'POST',
                {
                    groupId: bucketId,
                    memberId,
                    isAdmin,
                },
            );
            memberResponses.push(member);
        }
        const currentUser = yield select(userSelectors.getCurrentUser);
        const currentUserResponse = memberResponses.find(
            memberResponse => memberResponse.id === currentUser.id,
        );
        if (currentUserResponse) {
            yield put({
                type: bucketConstants.CURRENT_USER_ROLE_CHANGE_SUCCESS,
                payload: {
                    currentUserIsAdmin: currentUserResponse.isAdmin,
                    bucketId,
                },
            });
        }
        yield put({
            type: bucketConstants.CHANGE_USERS_ROLE_SUCCESS,
            payload: { members: memberResponses, bucketId },
        });
    } catch (e) {
        yield put({
            type: bucketConstants.CHANGE_USERS_ROLE_FAILURE,
        });
    }
}

function* watchChangeMembersRoles() {
    yield takeEvery(
        bucketConstants.CHANGE_USERS_ROLE_REQUEST,
        changeMembersRoles,
    );
}

function* removeMembers(action) {
    const { membersIds } = action.payload;
    const bucketId = yield select(bucketSelectors.getCurrentBucketId);
    try {
        for (let index = 0; index < membersIds.length; index += 1) {
            const memberId = membersIds[index];
            yield call(api, BackendPaths.bucketMember.getPath(), 'DELETE', {
                groupId: bucketId,
                memberId,
            });
        }
        yield put({
            type: bucketConstants.REMOVE_USERS_SUCCESS,
            payload: { membersIds, bucketId },
        });
    } catch (e) {
        yield put({
            type: bucketConstants.REMOVE_USERS_FAILURE,
        });
    }
}

function* watchRemoveMembers() {
    yield takeEvery(bucketConstants.REMOVE_USERS_REQUEST, removeMembers);
}

export default function*() {
    yield all([
        watchFetchBucketMembers(),
        watchAddBucketMembers(),
        watchUpdateBucket(),
        watchUpdateBucketSettings(),
        watchUpdateBucketAvatar(),
        watchChangeMembersRoles(),
        watchRemoveMembers(),
        watchAddMembersToBuckets(),
    ]);
}



// WEBPACK FOOTER //
// ./src/sagas/bucketSagas.js